import React, { useState, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Paper, Button, TextField, Box, Typography, IconButton, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { getIcon } from '../../../utils/IconHandler';
import TableRefreshButton from '../../../components/TableRefreshButton/TableRefreshButton';

const CompaniesTable = ({ companies = [], onAdd, loading, onRefresh, onEdit, onRevealCredentials, onDelete }) => {
  const { t } = useTranslation();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  
  const filteredCompanies = useMemo(() => {
    // Asegurarse de que companies sea un array
    if (!Array.isArray(companies)) {
      console.warn('Companies is not an array:', companies);
      return [];
    }
    // Filtrar empresas que tengan un id válido y aplicar búsqueda
    return companies
      .filter((company) => {
        // Asegurar que tenga un id válido (número o string numérico)
        const hasValidId = company?.id !== null && company?.id !== undefined && 
                          (typeof company.id === 'number' || (typeof company.id === 'string' && company.id.trim() !== ''));
        if (!hasValidId) {
          console.warn('Company without valid id:', company);
          return false;
        }
        return true;
      })
      .filter((company) => {
        const matchesSearch = !searchTerm || 
          company.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.domain?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          company.id?.toString().includes(searchTerm.toLowerCase());
        return matchesSearch;
      })
      .map((company) => ({
        ...company,
        id: typeof company.id === 'string' ? parseInt(company.id, 10) || company.id : company.id
      }));
  }, [companies, searchTerm]);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  const columns = [
    { 
      field: 'id', 
      headerName: 'ID', 
      flex: 0.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value || '-'}</Typography>
        </Box>
      )
    },
    { 
      field: 'name', 
      headerName: t('company_name'), 
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value || '-'}</Typography>
        </Box>
      )
    },
    { 
      field: 'domain', 
      headerName: t('domain') || 'Dominio (Login)', 
      flex: 1.5,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">{params.value || '-'}</Typography>
        </Box>
      )
    },
    {
      field: 'isActive',
      headerName: 'Estado',
      flex: 0.9,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Chip
            size="small"
            color={params.value ? 'success' : 'error'}
            label={params.value ? 'Activa' : 'Bloqueada'}
          />
        </Box>
      )
    },
    { 
      field: 'siteDomains', 
      headerName: t('site_domains') || 'Dominios del Sitio', 
      flex: 2,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const domains = params.value ? params.value.split(';').filter(d => d.trim()) : [];
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center', gap: 0.5, flexWrap: 'wrap' }}>
            {domains.length > 0 ? (
              domains.map((domain, idx) => (
                <Chip 
                  key={idx} 
                  label={domain.trim()} 
                  size="small" 
                  sx={{ fontSize: '0.75rem' }}
                />
              ))
            ) : (
              <Typography variant="body2">-</Typography>
            )}
          </Box>
        );
      }
    },
    { 
      field: 'createdAt', 
      headerName: t('created_at'), 
      flex: 2,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center' }}>
          <Typography variant="body2">{formatDate(params.value)}</Typography>
        </Box>
      )
    },
    {
      field: 'actions',
      headerName: t('actions') || 'Acciones',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'center', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => onEdit && onEdit(params.row)}
            sx={{ 
              width: 34,
              height: 34,
              minWidth: 34,
              minHeight: 34,
              flex: '0 0 34px',
              aspectRatio: '1 / 1',
              p: 0,
              borderRadius: '50% !important',
              color: 'grey.600',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: '#fff'
              }
            }}
          >
            {getIcon('settings')}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onRevealCredentials && onRevealCredentials(params.row)}
            sx={{
              width: 34,
              height: 34,
              minWidth: 34,
              minHeight: 34,
              flex: '0 0 34px',
              aspectRatio: '1 / 1',
              p: 0,
              borderRadius: '50% !important',
              color: 'grey.600',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: '#fff'
              }
            }}
          >
            {getIcon('lock')}
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDelete && onDelete(params.row)}
            sx={{
              width: 34,
              height: 34,
              minWidth: 34,
              minHeight: 34,
              flex: '0 0 34px',
              aspectRatio: '1 / 1',
              p: 0,
              borderRadius: '50% !important',
              color: 'error.main',
              '&:hover': {
                backgroundColor: 'error.main',
                color: '#fff'
              }
            }}
          >
            {getIcon('trash')}
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Paper sx={{ padding: 2, mt: 3 }}>
      {/* Filtros */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          label={t('search')}
          variant="outlined"
          size="small"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ width: 300 }}
          placeholder={t('search_by_name_domain')}
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TableRefreshButton loading={loading} onClick={onRefresh} />
          <Button variant="outlined" startIcon={getIcon('add')} onClick={onAdd}>
            {t('add_company')}
          </Button>
        </Box>
      </Box>

      <Box sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 1000 }}>
          <DataGrid
            loading={loading}
            rows={filteredCompanies}
            columns={columns}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            getRowId={(row) => {
              // Asegurar que siempre retornemos un id válido
              const id = row?.id;
              if (id === null || id === undefined) {
                console.error('Row without id:', row);
                return `row-${Math.random()}`; // Fallback temporal
              }
              return id;
            }}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default CompaniesTable;
